import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Download } from 'lucide-react';

const Footer: React.FC = () => (
  <footer className="bg-slate-950 text-slate-400 border-t border-white/5">
    <div className="container mx-auto max-w-6xl px-6 py-16">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

        {/* Brand */}
        <div className="sm:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-2.5 mb-4">
            <img src="/images/mbegu-traders-logo.png" alt="Mbegu" className="h-10 w-10 rounded-lg object-contain"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <div>
              <p className="text-white font-bold text-sm leading-tight">Mbegu</p>
              <p className="text-slate-500 text-[10px] leading-tight">Commercial Partners & Traders Ltd</p>
            </div>
          </div>
          <p className="text-slate-500 text-xs leading-relaxed mb-4 italic">...Growing Together 🌱</p>
          <div className="space-y-2 text-xs">
            <a href="tel:0721977639" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5 text-primary flex-shrink-0" />0721 977 639
            </a>
            <a href="tel:0711212616" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5 text-primary flex-shrink-0" />0711 212 616
            </a>
            <a href="mailto:mbegusbp@gmail.com" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail className="w-3.5 h-3.5 text-primary flex-shrink-0" />mbegusbp@gmail.com
            </a>
            <p className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
              <span>Molo, Nakuru County, Kenya<br />P.O Box 810 — 20106</span>
            </p>
          </div>
        </div>

        {/* Shop */}
        <div>
          <h4 className="text-white font-semibold text-sm mb-4">Products</h4>
          <ul className="space-y-2.5">
            {[['Vegetable Seedlings', '/products'], ['Tomatoes & Cabbages', '/products'],
              ['Chillies & Onions', '/products'], ['Herbs & Leafy Greens', '/products'],
              ['Grafted Fruit Trees', '/products'], ['Banana Varieties', '/products']].map(([l, to]) => (
              <li key={l}><Link to={to} className="text-xs hover:text-white transition-colors">{l}</Link></li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="text-white font-semibold text-sm mb-4">Company</h4>
          <ul className="space-y-2.5">
            {[['About Us', '/about'], ['Our Certifications', '/about'],
              ['Impact & Reach', '/impact'], ['Track Your Seedlings', '/track'],
              ['Fresh Produce Export', '/fresh-produce'],
              ['Invoice Manager', '/invoices'],
              ['Gallery', '/gallery'], ['Company Profile', '/company-profile'],
              ['Contact Us', '/contact'], ['Order Tracking', '/orders']].map(([l, to]) => (
              <li key={l}><Link to={to} className="text-xs hover:text-white transition-colors">{l}</Link></li>
            ))}
            <li>
              <a href="/MBEGU-Company-Profile.pdf.pdf" download="Mbegu-Company-Profile-2026.pdf"
                className="text-xs hover:text-white transition-colors flex items-center gap-1 text-green-500">
                <Download className="w-3 h-3" />Download PDF
              </a>
            </li>
          </ul>
        </div>

        {/* Payment */}
        <div>
          <h4 className="text-white font-semibold text-sm mb-4">Payment</h4>
          <div className="space-y-3">
            <div className="bg-green-900/20 border border-green-800/40 rounded-lg p-3 text-xs">
              <p className="text-green-400 font-bold mb-1">📱 M-Pesa Paybill</p>
              <p className="text-slate-300">Business: <strong className="text-white">247247</strong></p>
              <p className="text-slate-300">Account: <strong className="text-white">290524</strong></p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-xs">
              <p className="text-slate-300 font-bold mb-1">🏦 Equity Bank</p>
              <p className="text-slate-400">A/C: 0230286686451</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-xs">
              <p className="text-slate-300 font-bold mb-0.5">📟 USSD</p>
              <p className="text-slate-400">Dial <strong className="text-white">*483123#</strong></p>
            </div>
          </div>
        </div>
      </div>

      {/* Certifications bar */}
      <div className="border-t border-white/8 pt-6 mb-5">
        <div className="flex flex-wrap gap-3 justify-center">
          {['HCD Certified No. 1001934', 'KEPHIS Compliant', 'Cert. PVT-MP7TMDK', 'KRA PIN: P052578949C'].map(c => (
            <span key={c} className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 border border-slate-800 px-2.5 py-1 rounded-full">{c}</span>
          ))}
        </div>
      </div>

      <div className="border-t border-white/8 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-[11px] text-slate-600">© {new Date().getFullYear()} Mbegu Commercial Partners & Traders Limited. All rights reserved.</p>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
          Incorporated 16 March 2026 · Molo, Nakuru County, Kenya
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
